import type { Control } from "react-hook-form";
import { YesNoChoice } from "../../../../types/api.types";
import type {
  Person,
  SiteCategoriesSummaryResponse
} from "../../../../utils/backend-types";
import type { applyHighlightingToObjectFormFieldData } from "../../../../utils/react-hook-form/applyHighlightingToObjectFormFieldData";
import { FormFieldController } from "../../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { FormSection } from "../../../BuildingBlocks/Forms/FormSection/FormSection";
import type { PERSON_FORM_FIELD_DATA } from "../PersonForm.constants";

interface DeliveryAndInvoicesFieldsProps {
  control: Control<Person>;
  formFieldData: ReturnType<
    typeof applyHighlightingToObjectFormFieldData<
      Person,
      typeof PERSON_FORM_FIELD_DATA
    >
  >["formFieldData"];
  invoiceSuppliedAddressIsGeneralAddress:
    YesNoChoice.yes | YesNoChoice.no | null;
  invoiceSupplierIsGbr?: YesNoChoice.yes | YesNoChoice.no | null;
  isParagraph6EegActive: boolean;
  isSolarContractor?: boolean;
  personId: number;
  projectHasSitesWithInvoicing: boolean;
  siteCategories?: SiteCategoriesSummaryResponse;
}

export function DeliveryAndInvoicesFields({
  control,
  formFieldData,
  invoiceSuppliedAddressIsGeneralAddress,
  invoiceSupplierIsGbr,
  isParagraph6EegActive,
  isSolarContractor,
  personId,
  projectHasSitesWithInvoicing,
  siteCategories
}: DeliveryAndInvoicesFieldsProps) {
  const isFullFeedin = !!siteCategories?.is_full_feedin;
  const showSupplierFields =
    projectHasSitesWithInvoicing || isParagraph6EegActive;
  const showInvoiceSupplierIsGbrFields =
    invoiceSupplierIsGbr === YesNoChoice.no;
  const showInvoiceSuppliedAddressFields =
    invoiceSuppliedAddressIsGeneralAddress === YesNoChoice.no;

  return (
    <>
      {showSupplierFields &&
        (!isFullFeedin ? (
          <FormSection
            defaultOpen={isSolarContractor}
            isCollapsible={!isFullFeedin}
            label="Für die Lieferanten"
          >
            <SupplierFields
              control={control}
              formFieldData={formFieldData}
              isParagraph6EegActive={isParagraph6EegActive}
              personId={personId}
              showInvoiceSupplierIsGbrFields={showInvoiceSupplierIsGbrFields}
            />
          </FormSection>
        ) : (
          <SupplierFields
            control={control}
            formFieldData={formFieldData}
            isParagraph6EegActive={isParagraph6EegActive}
            personId={personId}
            showInvoiceSupplierIsGbrFields={showInvoiceSupplierIsGbrFields}
          />
        ))}

      {projectHasSitesWithInvoicing && (
        <>
          <FormSection
            defaultOpen={!isSolarContractor}
            isCollapsible
            label="Für die Belieferten"
          >
            <FormFieldController
              control={control}
              data={formFieldData.invoiceSuppliedCustomerNumber}
            />
            <FormFieldController
              control={control}
              data={formFieldData.invoiceSuppliedEmail}
            />
            <FormFieldController
              control={control}
              data={formFieldData.additionalInvoiceSuppliedEmails}
            />
            <FormFieldController
              control={control}
              data={formFieldData.invoiceSuppliedBusinessReference}
            />
            <FormFieldController
              control={control}
              data={formFieldData.invoiceSuppliedBank}
            />
            <FormFieldController
              control={control}
              data={formFieldData.invoiceSuppliedIban}
            />
            <FormFieldController
              control={control}
              data={formFieldData.invoiceSuppliedBic}
            />
            <FormFieldController
              control={control}
              data={formFieldData.invoiceRecipient}
            />
            <FormFieldController
              control={control}
              data={formFieldData.invoiceSuppliedAddressIsGeneralAddress}
            />
            {showInvoiceSuppliedAddressFields && (
              <>
                <FormFieldController
                  control={control}
                  data={formFieldData.invoiceSuppliedAddress}
                />
                <FormFieldController
                  control={control}
                  data={formFieldData.invoiceSuppliedAddressExtra}
                />
                <FormFieldController
                  control={control}
                  data={formFieldData.invoiceSuppliedZip}
                />
                <FormFieldController
                  control={control}
                  data={formFieldData.invoiceSuppliedCity}
                />
              </>
            )}
          </FormSection>

          <FormSection
            defaultOpen={!isSolarContractor}
            isCollapsible
            isSubSection
            label="Stammdaten für die Abrechnung"
          >
            <FormFieldController
              control={control}
              data={formFieldData.invoiceSuppliedDebitNumber}
            />
            <FormFieldController
              control={control}
              data={formFieldData.invoiceSuppliedAccountingArea}
            />
            <FormFieldController
              control={control}
              data={formFieldData.invoiceSuppliedTargetAccountingArea}
            />
            <FormFieldController
              control={control}
              data={formFieldData.invoiceSuppliedBusinessArea}
            />
            <FormFieldController
              control={control}
              data={formFieldData.invoiceSuppliedCostCenter}
            />
            <FormFieldController
              control={control}
              data={formFieldData.invoiceSuppliedPspElement}
            />
            <FormFieldController
              control={control}
              data={formFieldData.invoiceSuppliedProfitCenter}
            />
            <FormFieldController
              control={control}
              data={formFieldData.invoiceSuppliedGlobalId}
            />
          </FormSection>

          <FormSection
            defaultOpen={!isSolarContractor}
            isCollapsible
            isSubSection
            label="Individuelle Informationen auf der Rechnung für diesen Belieferten ausweisen"
          >
            <FormFieldController
              control={control}
              data={formFieldData.invoiceSuppliedInformationCategory}
            />
            <FormFieldController
              control={control}
              data={formFieldData.invoiceSuppliedInformation}
            />
          </FormSection>
        </>
      )}
    </>
  );
}

interface SupplierFieldsProps {
  control: Control<Person>;
  formFieldData: DeliveryAndInvoicesFieldsProps["formFieldData"];
  isParagraph6EegActive: boolean;
  personId: number;
  showInvoiceSupplierIsGbrFields: boolean;
}

function SupplierFields({
  control,
  formFieldData,
  isParagraph6EegActive,
  personId,
  showInvoiceSupplierIsGbrFields
}: SupplierFieldsProps) {
  return (
    <>
      {!isParagraph6EegActive && (
        <>
          <FormFieldController
            control={control}
            data={formFieldData.invoiceSupplierPhone}
          />
          <FormFieldController
            control={control}
            data={formFieldData.invoiceSupplierFax}
          />
          <FormFieldController
            control={control}
            data={formFieldData.invoiceSupplierEmail}
          />
          <FormFieldController
            control={control}
            data={formFieldData.invoiceSupplierUrl}
          />
        </>
      )}
      <FormFieldController
        control={control}
        data={formFieldData.invoiceSupplierCeo}
      />
      <FormFieldController
        control={control}
        data={formFieldData.invoiceSupplierIsGbr}
      />
      {showInvoiceSupplierIsGbrFields && (
        <>
          <FormFieldController
            control={control}
            data={formFieldData.invoiceSupplierCourt}
          />
          <FormFieldController
            control={control}
            data={formFieldData.invoiceSupplierRegisterNumber}
          />
        </>
      )}
      <FormFieldController
        control={control}
        data={formFieldData.invoiceSupplierLocation}
      />
      <FormFieldController
        control={control}
        data={{
          ...formFieldData.invoiceSupplierLogo,
          resourceId: personId
        }}
      />
      <FormFieldController
        control={control}
        data={formFieldData.invoiceSupplierSalesTaxId}
      />
      <FormFieldController
        control={control}
        data={formFieldData.invoiceSupplierTaxNumber}
      />
      {!isParagraph6EegActive && (
        <>
          <FormFieldController
            control={control}
            data={formFieldData.invoiceSupplierBank}
          />
          <FormFieldController
            control={control}
            data={formFieldData.invoiceSupplierIban}
          />
          <FormFieldController
            control={control}
            data={formFieldData.invoiceSupplierBic}
          />
          <FormFieldController
            control={control}
            data={formFieldData.invoiceSupplierCreditorId}
          />
        </>
      )}
    </>
  );
}
